/**
 * @hector/shared-types/auth — Zod schemas for identity & membership.
 *
 * Single source of truth for the auth/identity contract introduced in
 * TICKET-069. Used by:
 *   - apps/backend     : `.parse()` at the entry of every signup/invite route.
 *   - apps/legalworkspace : `z.infer<...>` instead of locally-redefined interfaces.
 *   - apps/addin-word  : same.
 *
 * Adding a field?
 *   1. Update the schema below.
 *   2. Update `docs/architecture/user-identity-and-roles.md`.
 *   3. Update the matching DB column (migration) if it lives in OVH.
 *
 * Renaming a field? Don't. Add a new one and deprecate the old via JSDoc.
 */
import { z } from 'zod';
import { SignupCrmRefSchema } from './crm-registration';
export * from './crm-registration';

// ────────────────────────────────────────────────────────────────────────────
// 1. Enums (the 3 orthogonal axes)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Who is this user to Hector? Stored on `public.users.platform_role` (OVH).
 * Derived at INSERT from email domain — never mutated by upsert.
 */
export const PlatformRoleSchema = z.enum([
  'customer',
  'hector_staff',
  'hector_super_admin',
]);
export type PlatformRole = z.infer<typeof PlatformRoleSchema>;

/**
 * What's this user's role in *this specific* cabinet? Stored on
 * `cabinet_members.cabinet_role` (OVH). 1 row per (user, cabinet).
 */
export const CabinetRoleSchema = z.enum(['admin', 'member']);
export type CabinetRole = z.infer<typeof CabinetRoleSchema>;

/**
 * Per-membership feature flags (jsonb). Known keys today:
 *   - `alpha_synthesis` (replaces legacy is_dev / can_see_synthesis)
 *
 * Open shape: any new boolean flag can be added without a migration.
 */
export const FeatureFlagsSchema = z.record(z.string(), z.boolean());
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

// ────────────────────────────────────────────────────────────────────────────
// 2. Domain objects
// ────────────────────────────────────────────────────────────────────────────

/**
 * Business identity (1 row in `public.users`). Decoupled from Supabase auth:
 * `id` = Supabase auth user id, but everything else lives in OVH.
 */
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  full_name: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  platform_role: PlatformRoleSchema,
  created_at: z.string(),
  updated_at: z.string(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Cabinet membership (1 row in `cabinet_members`). `cabinet_role`,
 * `license_exempt`, `feature_flags` are the three orthogonal axes
 * introduced by TICKET-069 (replacing is_admin/is_staff/is_dev/role).
 */
export const CabinetMemberSchema = z.object({
  id: z.string().uuid(),
  cabinet_id: z.string().uuid(),
  user_id: z.string().uuid(),

  // The 3 orthogonal axes
  cabinet_role: CabinetRoleSchema,
  license_exempt: z.boolean(),
  feature_flags: FeatureFlagsSchema,

  // Misc membership metadata
  title: z.string().nullable(),
  joined_at: z.string(),
  was_invited: z.boolean().optional(),
  workspace_first_time: z.boolean().optional(),
  addin_first_time: z.boolean().optional(),
  license_assigned_at: z.string().nullable().optional(),
  license_assigned_by: z.string().nullable().optional(),
  // Système d'annonces produit — ids d'annonces déjà vues (slugs opaques).
  seen_announcements: z.array(z.string()),
  // Parcours d'onboarding web : date de fin ou de fermeture, portée par
  // l'utilisateur (`users.onboarding_tour_closed_at`). Exposée par
  // `GET /cabinet-members/me` uniquement.
  onboarding_tour_closed_at: z.string().nullable().optional(),
  // Tutoriels déjà vus, portés par l'utilisateur (`users.tutorials_seen`,
  // migration 223). Exposés par `GET /cabinet-members/me` uniquement. Lus en
  // chaînes libres : un identifiant écrit par une version plus récente du
  // catalogue ne doit pas faire échouer la lecture du membre.
  tutorials_seen: z.array(z.string()).optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type CabinetMember = z.infer<typeof CabinetMemberSchema>;

/**
 * Enriched membership returned by `GET /cabinet-members/me` and `/list/:id`.
 * Combines membership + user identity (LEFT JOIN public.users) so the front
 * never has to call Supabase admin to resolve a display name.
 */
export const EnrichedCabinetMemberSchema = CabinetMemberSchema.extend({
  email: z.string().email().nullable(),
  full_name: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  platform_role: PlatformRoleSchema.nullable(),
});
export type EnrichedCabinetMember = z.infer<typeof EnrichedCabinetMemberSchema>;

// ────────────────────────────────────────────────────────────────────────────
// 3. HTTP request schemas (validated at route entry)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Body of `POST /api/auth/register` — standard email/password signup with
 * cabinet creation in the same transaction.
 */
export const RegisterRequestSchema = z.object({
  crm_ref: SignupCrmRefSchema,
  email: z.string().email({ message: 'Adresse email invalide.' }),
  password: z.string().min(8, { message: 'Le mot de passe doit contenir au moins 8 caractères.' }),
  /** Slug du lien d'inscription partenaire (`/inscription/uja`). Absent du catalogue → ignoré, jamais bloquant. */
  partner: z.string().max(40).optional(),
  fullName: z.string().min(1, { message: 'Le nom complet est requis.' }).max(200, { message: 'Le nom complet est trop long (200 caractères max).' }),
  cabinetName: z.string().min(1, { message: 'Le nom du cabinet est requis.' }).max(200, { message: 'Le nom du cabinet est trop long (200 caractères max).' }),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/**
 * Body of `POST /api/auth/complete-oauth-registration` — finalizes signup
 * for users who arrived via Google/Microsoft OAuth (no password set, full_name
 * comes from the OAuth provider's user_metadata).
 */
export const CompleteOAuthRegistrationRequestSchema = z.object({
  crm_ref: SignupCrmRefSchema,
  fullName: z.string().min(1, { message: 'Le nom complet est requis.' }).max(200, { message: 'Le nom complet est trop long (200 caractères max).' }),
  cabinetName: z.string().min(1, { message: 'Le nom du cabinet est requis.' }).max(200, { message: 'Le nom du cabinet est trop long (200 caractères max).' }),
});
export type CompleteOAuthRegistrationRequest = z.infer<
  typeof CompleteOAuthRegistrationRequestSchema
>;

/**
 * Body of `POST /api/cabinet-members/invite` — admin invites someone to
 * their cabinet. Two paths server-side:
 *   - invitee already exists in Supabase auth → direct INSERT into cabinet_members
 *   - invitee unknown → Supabase Auth invitation email + redirect to /complete-invitation
 *
 * `cabinet_role` is the canonical field. `is_admin` is accepted as a
 * legacy alias from older client builds and mapped server-side.
 */
export const InviteMemberRequestSchema = z
  .object({
    cabinetId: z.string().uuid(),
    email: z.string().email(),
    title: z.string().min(1).max(200).optional(),
    cabinet_role: CabinetRoleSchema.optional(),
    /** @deprecated Use `cabinet_role` instead. */
    is_admin: z.boolean().optional(),
    /** @deprecated The `role` field used to hold the human-readable title; use `title`. */
    role: z.string().optional(),
    inviteUrl: z.string().url().optional(),
  })
  .refine((v) => v.cabinet_role || typeof v.is_admin === 'boolean', {
    message: 'Either cabinet_role or legacy is_admin must be provided',
    path: ['cabinet_role'],
  });
export type InviteMemberRequest = z.infer<typeof InviteMemberRequestSchema>;

/**
 * Body of `POST /api/cabinet-members/complete-invitation` — invitee finishes
 * their profile after clicking the invitation email link.
 */
export const CompleteInvitationRequestSchema = z.object({
  cabinetId: z.string().uuid(),
  fullName: z.string().min(1, { message: 'Le nom complet est requis.' }).max(200, { message: 'Le nom complet est trop long (200 caractères max).' }),
  title: z.string().min(1).max(200).optional(),
  cabinet_role: CabinetRoleSchema.optional(),
  license_exempt: z.boolean().optional(),
  /** @deprecated Use `cabinet_role`. */
  is_admin: z.boolean().optional(),
  /** @deprecated Use `license_exempt`. */
  is_staff: z.boolean().optional(),
});
export type CompleteInvitationRequest = z.infer<
  typeof CompleteInvitationRequestSchema
>;

/**
 * Body of `PATCH /api/cabinet-members/:memberId` — admin updates a member.
 * All fields optional; only those present are applied.
 */
export const UpdateCabinetMemberRequestSchema = z.object({
  cabinet_role: CabinetRoleSchema.optional(),
  license_exempt: z.boolean().optional(),
  feature_flags: FeatureFlagsSchema.optional(),
  title: z.string().min(1).max(200).nullable().optional(),
});
export type UpdateCabinetMemberRequest = z.infer<
  typeof UpdateCabinetMemberRequestSchema
>;

// WHY : le nom (du cabinet ou de l'avocat) est recopié dans le profil mémoire seedé, injecté dans le
// contexte système de l'agent sous la clôture `<user_memory>` ; une seule ligne sans `<` ni `>`
// l'empêche d'en sortir ou d'y glisser une consigne.
const SINGLE_LINE_WITHOUT_FENCE = /^[^\p{Cc}\u2028\u2029<>]*$/u;

/**
 * Body of `PATCH /api/cabinets/:cabinetId/name` — the cabinet admin renames their
 * cabinet (Paramètres → Profil → Cabinet). Same length bounds as `cabinetName` at
 * registration, plus a single line without `<` or `>`; trimmed so a name made of
 * spaces is rejected. `strict`: any other
 * key is a client bug, not something to ignore silently.
 */
export const UpdateCabinetNameRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(SINGLE_LINE_WITHOUT_FENCE, 'Le nom du cabinet tient sur une ligne, sans les caractères < et >'),
  })
  .strict();
export type UpdateCabinetNameRequest = z.infer<typeof UpdateCabinetNameRequestSchema>;

/**
 * Body of `PATCH /api/cabinet-members/me/full-name` — the signed-in user renames
 * themselves (`users.full_name`). Same single-line rule as the cabinet name: the
 * name is copied into the seeded `user_profile` memory block. `.strict()`: the
 * target is always the caller, an extra key such as `userId` is a client bug.
 */
export const UpdateMyFullNameRequestSchema = z
  .object({
    fullName: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(SINGLE_LINE_WITHOUT_FENCE, 'Le nom complet tient sur une ligne, sans les caractères < et >'),
  })
  .strict();
export type UpdateMyFullNameRequest = z.infer<typeof UpdateMyFullNameRequestSchema>;

// P2-1 : ids = slugs datés bornés (anti-payload abusif / injection de bruit).
const AnnouncementIdSchema = z.string().regex(/^[a-z0-9-]{3,80}$/);
/**
 * Body of `PATCH /api/cabinet-members/announcements-seen` — marks announcement
 * ids as seen for the current user's membership (union, dédupliqué côté DB).
 */
export const MarkAnnouncementsSeenRequestSchema = z.object({
  ids: z.array(AnnouncementIdSchema).min(1).max(200),
});
export type MarkAnnouncementsSeenRequest = z.infer<
  typeof MarkAnnouncementsSeenRequestSchema
>;

/**
 * Catalogue des tutoriels dont l'app web retient, par utilisateur, qu'ils ont
 * été vus (`users.tutorials_seen`). Un identifiant est permanent : le renommer
 * ferait revoir le tutoriel à tous ceux qui l'ont déjà vu.
 *
 * - `papier-a-en-tete` : accueil animé de Personnalisation → En-tête et pied de
 *   page, ouvert d'office à la première visite.
 */
export const TUTORIAL_CATALOG = ['papier-a-en-tete'] as const;
export const TutorialIdSchema = z.enum(TUTORIAL_CATALOG);
export type TutorialId = z.infer<typeof TutorialIdSchema>;

/**
 * Body of `PATCH /api/cabinet-members/tutorials-seen` (ajout idempotent) et
 * `PATCH /api/cabinet-members/tutorials-unseen` (retrait, pour rejouer). Strict
 * à l'écriture : seul un identifiant du catalogue entre en base.
 */
export const TutorialSeenRequestSchema = z.object({ id: TutorialIdSchema }).strict();
export type TutorialSeenRequest = z.infer<typeof TutorialSeenRequestSchema>;

// ────────────────────────────────────────────────────────────────────────────
// 4. HTTP response schemas (kept in sync with backend handlers)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Response of `GET /cabinet-members/me`. Returns null for users without a
 * cabinet membership yet (typical during signup, before /complete-oauth
 * finishes).
 */
export const CabinetMemberMeResponseSchema =
  EnrichedCabinetMemberSchema.nullable();
export type CabinetMemberMeResponse = z.infer<
  typeof CabinetMemberMeResponseSchema
>;

/**
 * Response of `GET /cabinet-members/list/:cabinetId`. Used by the Settings
 * "Members" tab.
 */
export const CabinetMembersListResponseSchema = z.array(
  EnrichedCabinetMemberSchema,
);
export type CabinetMembersListResponse = z.infer<
  typeof CabinetMembersListResponseSchema
>;

// ────────────────────────────────────────────────────────────────────────────
// 5. Admin actions (TICKET-113) — super-admin cross-cabinet operations
// ────────────────────────────────────────────────────────────────────────────

/**
 * Whitelist of known feature flag keys used by `cabinet_members.feature_flags`.
 *
 * Why a closed enum here even though `FeatureFlagsSchema` (above) is open:
 * - The open `record(string, boolean)` shape lets the backend store any future
 *   flag without a shared-types release (forward compat).
 * - But the **admin UI** must only let super-admins toggle keys that we
 *   recognise — typo/unknown keys are rejected at the API boundary so we
 *   don't pollute the jsonb with garbage. The catalog is the single source
 *   of truth shown by the Drawer multiselect (cf. TICKET-113 spec §6.5).
 *
 * Adding a new flag: append it here AND document its semantics next to the
 * code that reads it. Removing a flag: deprecate via JSDoc, never reuse a key.
 */
export const FEATURE_FLAG_CATALOG = [
  'alpha_synthesis',
  'beta_redaction_web',
  'force_paywall',
  // Parcours d'onboarding de l'app web (bulles guidées : créer un dossier,
  // déposer des pièces, tamponner, lancer une rédaction). Le parcours démarre
  // de lui-même pour les comptes créés depuis le 2026-10-05 ; ce feature_flag le
  // FORCE sur un compte plus ancien (démos, tests internes). Lu uniquement
  // côté front par `features/onboarding-tour/useOnboardingTourFlag.ts`.
  'beta_onboarding_tour',
  // Contacts, Agenda et Finance, portés depuis le tag `staging-archive-2026-11-02`
  // (#2629, décisions #2665 et #2666). Inscrits AVANT le portage : la base staging
  // porte déjà `beta_agenda` et `beta_finance` sur des membres, et une clé hors
  // catalogue fait échouer `GET /api/admin/users` (lecture stricte). Fermés par
  // défaut ; chaque écran porté lit son feature_flag, documenté à côté du lecteur.
  'beta_contacts',
  'beta_agenda',
  'beta_finance',
] as const;
export const FeatureFlagSchema = z.enum(FEATURE_FLAG_CATALOG);
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;

/**
 * Tells whether the manual paywall must block a member, from the raw
 * `cabinet_members.feature_flags` value returned by `GET /cabinet-members/me`.
 *
 * Deliberately takes `unknown` and fails open: a wrongly blocked paying
 * customer costs more than a late block. Only the boolean literal `true` on
 * the `force_paywall` key of a plain object activates it — `"true"`, `1`, an
 * array-shaped jsonb, `null` or a missing column all leave the paywall off.
 */
export function isManualPaywallActive(featureFlags: unknown): boolean {
  // typeof null === 'object' and arrays are objects: both must be ruled out.
  if (featureFlags === null || typeof featureFlags !== 'object' || Array.isArray(featureFlags)) {
    return false;
  }
  return (featureFlags as Record<string, unknown>).force_paywall === true;
}

/**
 * Flags retirés du catalogue éditable mais encore tolérés au PARSING des rows
 * persistés en DB. Sans ça, retirer un flag du catalogue strict casse
 * `GET /api/admin/users` dès qu'un `cabinet_members.feature_flags` contient
 * encore la clé legacy (Zod `z.enum` rejette les keys hors enum).
 *
 * Cycle de vie d'un flag retiré :
 *   1. Retirer du catalogue actif `FEATURE_FLAG_CATALOG` (cesse d'être éditable
 *      depuis l'UI super-admin, cesse d'être writable via `PATCH …/feature-flags`).
 *   2. L'ajouter ici (`DEPRECATED_FEATURE_FLAGS`) pour que les rows existants
 *      en DB continuent de parser jusqu'à backfill.
 *   3. Lancer le SQL de backfill (cf. `apps/backend/scripts/cleanup-feature-flag-*.sql`).
 *   4. Une fois la DB nettoyée, retirer la clé d'ici aussi.
 */
export const DEPRECATED_FEATURE_FLAGS = [
  // Retirés le 2026-11-03 (décision 5 de #2665) : réservés, jamais lus par le code.
  // Aucune ligne ne les portait en prod ni sur staging ce jour-là ; tolérés à la
  // lecture le temps qu'aucun ancien front ou backend encore déployé ne puisse
  // les écrire, puis retirés d'ici sans purge si un nouveau contrôle reste à 0.
  'beta_doc_gen',
  'beta_legifrance_v2',
  'debug_traces',
  'pdf_split_admin',
  // Retiré le 2026-10-12 : le connecteur courrier et Correspondances sont ouverts
  // à tous. Les anciennes lignes restent lisibles sans autoriser de nouvelle
  // écriture de cette clé ; aucun nettoyage DB préalable au déploiement.
  'beta_mailbox_connector',
  'beta_plan_detaille_v2',
  // Retiré le 2026-06-24 : le tool `build_discussion_plan` + pipeline
  // `casedisco_research` (worker fire-and-forget + LegalPlanner autonome +
  // Sonnet rewriter) ont été supprimés au profit d'une orchestration
  // in-loop par le MasterLoop casedisco-v3 (qui driver moyen_builder via
  // invoke_subagent en gardant le contexte conversationnel). Le flag est
  // toléré au PARSING tant qu'un row le porte encore — purge à prévoir.
  'beta_casedisco_planner',
  // Retiré le 2026-05-16 : la modale d'annonces produit est passée en GA
  // (plus de gating, affichée à toute la base). Toléré au PARSING tant
  // qu'un row le porte encore (1 membre preview) — purge via
  // `apps/backend/scripts/cleanup-feature-flag-beta-announcements.mjs`,
  // puis retrait d'ici dans une PR ultérieure.
  'beta_announcements',
  // Retiré le 2026-06-10 : la Case Wiki (Legal Wiki Builder) est passée en GA
  // (plus de gating — dashboard + 3 outils agent + routes pipeline ouverts à
  // tous). Toléré au PARSING tant qu'un row le porte encore — purge via
  // `apps/backend/scripts/cleanup-feature-flag-legal-wiki-builder.mjs` (à lancer
  // POST-DEPLOY), puis retrait d'ici dans une PR ultérieure.
  'legal_wiki_builder',
  // Retiré le 2026-07-02 : la spécialisation "Droit Public" du wizard d'acte
  // (add-in Word) est passée en GA — plus de gate de visibilité/clickabilité,
  // l'entrée est ouverte à tous (chip "Nouveau"). Toléré au PARSING tant qu'un
  // row de cabinet pilote le porte encore ; purge DB puis retrait d'ici dans
  // une PR ultérieure.
  'beta_droit_public',
  // Retiré le 2026-09-21 : la spécialisation « Droit Immobilier et de la
  // Construction » du wizard d'acte (add-in Word) est passée en GA — plus de
  // verrou de clickabilité, l'entrée est ouverte à tous (chip « Nouveau »).
  // Toléré au PARSING tant qu'un row de cabinet pilote le porte encore ; purge
  // DB puis retrait d'ici dans une PR ultérieure.
  'beta_droit_immobilier',
] as const;

const FeatureFlagKeyParseSchema = z.enum([
  ...FEATURE_FLAG_CATALOG,
  ...DEPRECATED_FEATURE_FLAGS,
]);

/**
 * Strict shape de `cabinet_members.feature_flags` pour les WRITES (UI super-admin,
 * PATCH /api/admin/users/:userId/feature-flags). Rejette les keys hors catalogue
 * actif — empêche d'écrire des flags inconnus ou deprecated.
 */
export const FeatureFlagsRecordSchema = z
  .record(FeatureFlagSchema, z.boolean())
  .default({});
export type FeatureFlagsRecord = z.infer<typeof FeatureFlagsRecordSchema>;

/**
 * Shape relâchée pour les READS depuis la DB. Tolère les keys deprecated tant
 * qu'elles existent encore dans les rows persistés. À utiliser pour les
 * réponses de `GET /api/admin/users` et toute lecture cross-cabinet.
 */
export const FeatureFlagsRecordReadSchema = z
  .record(FeatureFlagKeyParseSchema, z.boolean())
  .default({});
export type FeatureFlagsRecordRead = z.infer<typeof FeatureFlagsRecordReadSchema>;

/**
 * Cross-cabinet user view returned by `GET /api/admin/users`. Joins
 * `public.users` with `cabinet_members` (LEFT JOIN — a user may exist
 * without a current membership) and `cabinets` (for the cabinet name).
 */
export const EnrichedUserWithCabinetSchema = z.object({
  id: z.string().uuid(),
  // email/avatar_url are NOT validated as strict formats here :
  // la table public.users prod contient des données héritées (ex: emails
  // mal formés sur les comptes très anciens, avatar_url avec des paths
  // relatifs). Pour le tableau de bord super-admin on veut afficher les
  // données telles quelles sans rejeter la réponse entière sur 1 email
  // invalide. Les writes (signup, invite) restent strict côté
  // RegisterRequestSchema / InviteMemberRequestSchema.
  email: z.string(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  platform_role: PlatformRoleSchema,
  cabinet_id: z.string().uuid().nullable(),
  cabinet_name: z.string().nullable(),
  cabinet_role: CabinetRoleSchema.nullable(),
  license_exempt: z.boolean(),
  // Read-shape : tolère les flags deprecated qui peuvent encore exister en DB.
  feature_flags: FeatureFlagsRecordReadSchema,
  created_at: z.string(),
});
export type EnrichedUserWithCabinet = z.infer<
  typeof EnrichedUserWithCabinetSchema
>;

/**
 * Response of `GET /api/admin/users` — paginated list of cross-cabinet users.
 */
export const AdminUsersListResponseSchema = z.object({
  users: z.array(EnrichedUserWithCabinetSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type AdminUsersListResponse = z.infer<
  typeof AdminUsersListResponseSchema
>;

/**
 * Body of `PATCH /api/admin/users/:userId/platform-role`.
 * Backend additionally enforces: not self, not last super_admin (cf. spec §5).
 */
export const UpdatePlatformRoleRequestSchema = z.object({
  platform_role: PlatformRoleSchema,
});
export type UpdatePlatformRoleRequest = z.infer<
  typeof UpdatePlatformRoleRequestSchema
>;

/**
 * Body of `PATCH /api/admin/users/:userId/cabinet-role`.
 * Backend additionally enforces: not self.
 */
export const UpdateCabinetRoleRequestSchema = z.object({
  cabinet_role: CabinetRoleSchema,
});
export type UpdateCabinetRoleRequest = z.infer<
  typeof UpdateCabinetRoleRequestSchema
>;

/**
 * Body of `PATCH /api/admin/users/:userId/license-exempt` — toggle a free seat.
 */
export const UpdateLicenseExemptRequestSchema = z.object({
  license_exempt: z.boolean(),
});
export type UpdateLicenseExemptRequest = z.infer<
  typeof UpdateLicenseExemptRequestSchema
>;

/**
 * Body of `PATCH /api/admin/users/:userId/feature-flags` — full replace
 * (not merge): the posted object overwrites `cabinet_members.feature_flags`.
 */
export const UpdateFeatureFlagsRequestSchema = z.object({
  feature_flags: FeatureFlagsRecordSchema,
});
export type UpdateFeatureFlagsRequest = z.infer<
  typeof UpdateFeatureFlagsRequestSchema
>;

/**
 * Body of `POST /api/admin/users/me/cabinet-swap` — UPDATE the caller's
 * `cabinet_members.cabinet_id` to switch their workspace. Super-admin only.
 */
export const CabinetSwapRequestSchema = z.object({
  targetCabinetId: z.string().uuid(),
});
export type CabinetSwapRequest = z.infer<typeof CabinetSwapRequestSchema>;

/**
 * Body of `POST /api/admin/cabinets/merge` — move every cabinet-scoped row
 * from `sourceCabinetId` into `targetCabinetId`. The source cabinet stays in
 * place (empty) — deletion is a separate manual step. Super-admin only.
 *
 * `dryRun: true` returns the per-table row counts WITHOUT executing the
 * UPDATEs. Used by the UI to render a preview before confirmation.
 */
export const MergeCabinetRequestSchema = z
  .object({
    sourceCabinetId: z.string().uuid(),
    targetCabinetId: z.string().uuid(),
    dryRun: z.boolean().optional().default(false),
  })
  .refine((v) => v.sourceCabinetId !== v.targetCabinetId, {
    message: 'sourceCabinetId and targetCabinetId must differ',
    path: ['targetCabinetId'],
  });
export type MergeCabinetRequest = z.infer<typeof MergeCabinetRequestSchema>;

/**
 * Per-table row count emitted by the merge endpoint. Keys mirror the SQL
 * tables that get rewritten — useful for the UI preview AND for the post-run
 * audit toast. `cabinet_members_deduped` counts the rows DROPPED because the
 * user already had a membership in the target cabinet.
 */
export const MergeCabinetCountsSchema = z.object({
  cases: z.number().int().nonnegative(),
  generated_documents: z.number().int().nonnegative(),
  casediscovery_conversations: z.number().int().nonnegative(),
  conversation_user_state: z.number().int().nonnegative(),
  casediscovery_conversation_related_cases: z.number().int().nonnegative(),
  case_document_embeddings: z.number().int().nonnegative(),
  enhanced_jurisprudence_research: z.number().int().nonnegative(),
  bordereaux: z.number().int().nonnegative(),
  pipeline_step_log: z.number().int().nonnegative(),
  job_events: z.number().int().nonnegative(),
  cabinet_members: z.number().int().nonnegative(),
  cabinet_members_deduped: z.number().int().nonnegative(),
  prompts_library_add_in: z.number().int().nonnegative(),
  system_prompts_library: z.number().int().nonnegative(),
  legal_templates: z.number().int().nonnegative(),
  template_packs: z.number().int().nonnegative(),
});
export type MergeCabinetCounts = z.infer<typeof MergeCabinetCountsSchema>;

export const MergeCabinetResponseSchema = z.object({
  success: z.boolean(),
  dryRun: z.boolean(),
  sourceCabinetId: z.string().uuid(),
  targetCabinetId: z.string().uuid(),
  counts: MergeCabinetCountsSchema,
});
export type MergeCabinetResponse = z.infer<typeof MergeCabinetResponseSchema>;

/**
 * Body of `POST /api/admin/cabinets/:cabinetId/deletion` — permanent deletion
 * of a cabinet, its single member and all their data (OVH rows, S3 objects,
 * Supabase Auth account). Super-admin only.
 *
 * `dryRun: true` returns the preview (what would be destroyed, and whether
 * anything blocks it) without deleting. A real run must echo the cabinet name
 * in `confirmCabinetName`: the server compares it to the stored name, so the
 * typed confirmation of the UI cannot be bypassed by calling the API directly.
 *
 * `reason` is stored in the durable deletion record (`cabinet_deletions`),
 * which outlives the cabinet. It must reference the request (ticket, date),
 * never the name or e-mail of the person being erased.
 */
export const CabinetDeletionRequestSchema = z
  .object({
    dryRun: z.boolean().optional().default(false),
    confirmCabinetName: z.string().max(500).optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type CabinetDeletionRequest = z.infer<typeof CabinetDeletionRequestSchema>;

/**
 * Why a cabinet cannot be deleted through the Super Admin screen. Each code is
 * a case the first version deliberately refuses rather than guesses:
 * - `cabinet_has_no_member`: a cabinet emptied by a cabinet merge still holds
 *   rows (Canvas drafts, mailbox connections) of accounts moved elsewhere.
 * - `cabinet_has_multiple_members`: the dossiers of the other members would
 *   need an owner — out of scope, only cabinets of exactly one member are
 *   deletable.
 * - `member_is_not_customer`: Hector staff accounts are never erased here.
 * - `member_belongs_to_other_cabinet`: the account must survive in the other
 *   cabinet, so it cannot be deleted with this one.
 * - `member_is_requester`: a super admin cannot delete their own account.
 * - `member_has_data_in_other_cabinet`: the account left rows in a cabinet it
 *   used to belong to (cabinet swap, accepted invitation); they are that
 *   cabinet's data and must be dealt with there first.
 * - `cabinet_manages_billing_for_other_cabinets`: other cabinets are billed
 *   through this one and would silently lose their billing link.
 * - `server_not_configured`: the server lacks the storage or Supabase
 *   configuration needed to finish a deletion; refused before any row goes.
 */
export const CABINET_DELETION_BLOCKER_CODES = [
  'cabinet_has_no_member',
  'cabinet_has_multiple_members',
  'member_is_not_customer',
  'member_belongs_to_other_cabinet',
  'member_is_requester',
  'member_has_data_in_other_cabinet',
  'cabinet_manages_billing_for_other_cabinets',
  'server_not_configured',
] as const;
export const CabinetDeletionBlockerCodeSchema = z.enum(CABINET_DELETION_BLOCKER_CODES);
export type CabinetDeletionBlockerCode = z.infer<typeof CabinetDeletionBlockerCodeSchema>;

export const CabinetDeletionPreviewSchema = z.object({
  cabinet: z.object({ id: z.string().uuid(), name: z.string() }),
  members: z.array(
    z.object({
      userId: z.string().uuid(),
      email: z.string().nullable(),
      fullName: z.string().nullable(),
      platformRole: z.string().nullable(),
      membershipCount: z.number().int().nonnegative(),
    }),
  ),
  counts: z.object({
    cases: z.number().int().nonnegative(),
    documents: z.number().int().nonnegative(),
    conversations: z.number().int().nonnegative(),
  }),
  /**
   * Billing rows of the cabinet. A live Stripe subscription does NOT block the
   * deletion and is NOT cancelled by it: the UI shows it as a warning and the
   * super admin cancels it in Stripe.
   */
  subscriptions: z.array(
    z.object({
      status: z.string().nullable(),
      stripeSubscriptionId: z.string().nullable(),
      stripeCustomerId: z.string().nullable(),
    }),
  ),
  blockers: z.array(
    z.object({ code: CabinetDeletionBlockerCodeSchema, message: z.string() }),
  ),
});
export type CabinetDeletionPreview = z.infer<typeof CabinetDeletionPreviewSchema>;

/**
 * Outcome of a real deletion. `completed_with_attention` means the database
 * rows are gone (the transaction committed) but a later step needs a human:
 * an S3 object or a Supabase Auth account could not be removed, no file was
 * found although the cabinet had pièces, or rows still reference the deleted
 * identifiers. `deletionId` is the `cabinet_deletions`
 * row holding the full report.
 */
export const CabinetDeletionResultSchema = z.object({
  deletionId: z.string().uuid(),
  status: z.enum(['completed', 'completed_with_attention']),
  deletedRowCount: z.number().int().nonnegative(),
  storage: z.array(
    z.object({
      // Only the primary bucket is purged; the replication bucket keeps the
      // files three months on purpose (see `remainingCopies`).
      label: z.literal('primary'),
      status: z.enum(['completed', 'completed_with_errors', 'no_versions_found', 'missing_configuration']),
      versionsDeleted: z.number().int().nonnegative(),
      errorCount: z.number().int().nonnegative(),
    }),
  ),
  auth: z.array(
    z.object({
      userId: z.string().uuid(),
      status: z.enum(['deleted', 'already_absent', 'error']),
    }),
  ),
  residuals: z.array(
    z.object({ table: z.string(), column: z.string(), rows: z.number().int().positive() }),
  ),
  /** French sentences listing what the super admin still has to do by hand. */
  manualFollowUps: z.array(z.string()),
  /**
   * French sentences listing the copies that outlive the deletion by design
   * (replication bucket kept three months, database backups, caches). Shown
   * with every result so "terminé" is not read as "nothing is left anywhere".
   */
  remainingCopies: z.array(z.string()),
});
export type CabinetDeletionResult = z.infer<typeof CabinetDeletionResultSchema>;

export const CabinetDeletionResponseSchema = z.object({
  success: z.boolean(),
  dryRun: z.boolean(),
  preview: CabinetDeletionPreviewSchema,
  /** `null` on a dry run. */
  result: CabinetDeletionResultSchema.nullable(),
});
export type CabinetDeletionResponse = z.infer<typeof CabinetDeletionResponseSchema>;
